// CRM layer — the CLASSIFICATION GATE (Batch 3).
//
// Every answered call (and every missed call that got a follow-up) now creates
// an opportunity automatically, which means the pipeline receives rows that are
// not opportunities at all: someone asking a question, chasing an existing
// request, or making a general enquiry. This screen is where a human tells
// those apart, and it is the ONLY thing an agent can do with such a row.
//
// WHY A FULL-PAGE GATE RATHER THAN A BANNER: the opportunity page invites work
// — advance the stage, write details, message the team. Doing any of that on an
// untriaged row is wasted effort if the call turns out to be a question, and a
// banner is dismissible by ignoring it. Replacing the page makes the decision
// the only available action, and it takes one click to clear.
//
// The four choices are presented with EQUAL visual weight on purpose. "فرصة
// حقيقية" carries the accent tint because it is the only answer that continues
// the journey, but it is not a louder button: a misclassification here pollutes
// conversion statistics permanently, and classification is one-way (the backend
// refuses to re-classify), so nudging the answer would be expensive.

import React, { useState } from 'react';
import {
  FiPhoneIncoming, FiPhoneMissed, FiUser, FiClock, FiTag, FiCheckCircle, FiArchive
} from 'react-icons/fi';
import { crmClassify, CLASSIFY_CHOICES, typeLabel, fmtDateTime } from './crmApi';

/**
 * @param {object}   opportunity  the pending_classification row
 * @param {string}   clientName   resolved client name, if known
 * @param {string}   agentName    resolved name of the assigned agent
 * @param {Array}    calls        the client's recent calls (for context)
 * @param {boolean}  canClassify  assigned agent or admin (backend re-checks)
 * @param {function} onClassified (classification, archived) => void
 * @param {function} onBack       leave without deciding
 */
const CrmClassifyGate = ({
  opportunity: o, clientName, agentName, calls = [], canClassify,
  onClassified, onBack
}) => {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  // The call this opportunity was created from, when it is still in the log.
  const sourceCall = calls.find((c) => c.id === o.source_call_id)
    || calls.find((c) => c.call_id === o.source_call_id)
    || calls[0]
    || null;

  const choose = async (choice) => {
    if (busy) return;              // one decision at a time; it is irreversible
    setBusy(choice.key);
    setError('');
    try {
      await crmClassify(o.id, choice.key);
      onClassified(choice.key, choice.archives);
    } catch (e) {
      setError(e.response?.data?.error || 'تعذر حفظ التصنيف');
      setBusy('');
    }
  };

  return (
    <div className="crm-scope crm-calm crm-gate">
      <div className="crm-gate-card">
        <div className="crm-gate-head">
          <span className="crm-pill crm-pill-pending">بانتظار التصنيف</span>
          <h1>ما نوع هذا التواصل؟</h1>
          <p>
            أنشأ النظام هذا السجل تلقائياً من مكالمة. حدّد نوعه أولاً — الفرص
            الحقيقية فقط هي التي تدخل مسار المتابعة وتُحتسب في الإحصائيات.
          </p>
        </div>

        {/* Context: everything needed to answer, without leaving the page. */}
        <div className="crm-gate-ctx">
          <div>
            <span className="k"><FiUser size={11} /> العميل</span>
            <span className="v">{clientName || o.client_name || 'غير مسجل'}</span>
          </div>
          <div>
            <span className="k">رقم الجوال</span>
            <span className="v ltr">{o.phone_number}</span>
          </div>
          <div>
            <span className="k"><FiUser size={11} /> الموظف</span>
            <span className="v">{agentName || o.assigned_to_extension || 'غير مسند'}</span>
          </div>
          <div>
            <span className="k"><FiClock size={11} /> وقت الإنشاء</span>
            <span className="v">{fmtDateTime(o.created_at)}</span>
          </div>
          {sourceCall && (
            <div>
              <span className="k">
                {sourceCall.status === 'answered'
                  ? <FiPhoneIncoming size={11} />
                  : <FiPhoneMissed size={11} />} المكالمة المصدر
              </span>
              <span className="v">
                {sourceCall.status === 'answered' ? 'مردود عليها' : 'فائتة ثم متابَعة'}
                {sourceCall.started_at ? ` · ${fmtDateTime(sourceCall.started_at)}` : ''}
              </span>
            </div>
          )}
          <div>
            <span className="k"><FiTag size={11} /> نوع الطلب</span>
            <span className="v">{typeLabel(o.opportunity_type)}</span>
          </div>
        </div>

        {error && <div className="crm-modal-err">{error}</div>}

        {canClassify ? (
          <>
            <div className="crm-gate-grid">
              {CLASSIFY_CHOICES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`crm-gate-choice${c.key === 'real' ? ' is-real' : ''}`}
                  disabled={!!busy}
                  onClick={() => choose(c)}
                >
                  <span className="lbl">{busy === c.key ? 'جاري الحفظ...' : c.label}</span>
                  <span className="hint">{c.hint}</span>
                  <span className="tag">
                    {c.archives
                      ? <><FiArchive size={10} /> يُؤرشف مباشرة</>
                      : <><FiCheckCircle size={10} /> يفتح مسار المتابعة</>}
                  </span>
                </button>
              ))}
            </div>

            <div className="crm-gate-foot">
              <span className="crm-gate-note">
                التصنيف يتم مرة واحدة ولا يمكن تغييره لاحقاً. متابعات الطلبات
                السابقة تُؤرشف ولا تُحتسب ضمن الفرص أو الخسائر.
              </span>
              <span style={{ flex: 1 }} />
              {onBack && (
                <button type="button" className="crm-btn crm-btn-soft"
                  style={{ width: 'auto', padding: '8px 14px' }} onClick={onBack}>
                  لاحقاً
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="crm-gate-locked">
            هذا السجل مُسند إلى {agentName || o.assigned_to_extension || 'موظف آخر'} —
            التصنيف متاح للموظف المسؤول أو المدير فقط.
          </div>
        )}
      </div>
    </div>
  );
};

export default CrmClassifyGate;
